import useIsEqual from 'hook/useIsEqual'
import { useEffect, useMemo, useRef, useState } from 'react'

interface UseParamsTrackerOptions<T> {
	params: T
	onParamsChange?: () => void
}

interface UseParamsTrackerReturn {
	shouldResetPagination: boolean
	isParamsChanged: boolean
}

export const useParamsTracker = <T extends Record<string, any>>({
	params,
	onParamsChange,
}: UseParamsTrackerOptions<T>): UseParamsTrackerReturn => {
	const [shouldResetPagination, setShouldResetPagination] = useState(false)

	// Ref для хранения предыдущих параметров
	const previousParamsRef = useRef(params)

	// Мемоизируем текущие параметры для стабильности
	const currentParams = useMemo(() => params, [params])

	// Сравниваем с предыдущими параметрами
	const isEqualToPrevious = useIsEqual(previousParamsRef.current, currentParams)

	// Отслеживание изменений параметров
	useEffect(() => {
		// Если параметры НЕ равны предыдущим, значит что-то изменилось
		if (!isEqualToPrevious) {
			setShouldResetPagination(true)
			// Вызываем callback если передан
			onParamsChange?.()
			// Обновляем предыдущие параметры
			previousParamsRef.current = currentParams
		}
	}, [isEqualToPrevious, currentParams, onParamsChange])

	// Сбрасываем флаг shouldResetPagination после применения
	useEffect(() => {
		if (shouldResetPagination) {
			const timer = setTimeout(() => setShouldResetPagination(false), 0)
			return () => clearTimeout(timer)
		}
	}, [shouldResetPagination])

	return {
		shouldResetPagination,
		isParamsChanged: !isEqualToPrevious,
	}
}
